import { useState, useRef, useEffect } from "react";
import { Link } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { CheckSquare } from "lucide-react";
import clsx from "clsx";
import { api } from "@/lib/api";
import { fmtDate } from "@/lib/format";
import { HealthBadge } from "./badges";
import type { TaskItem } from "@shared/types";

export function TaskMenu() {
  const [open, setOpen] = useState(false);
  const ref = useRef<HTMLDivElement>(null);

  const { data } = useQuery({
    queryKey: ["tasks", "peek"],
    queryFn: () => api.get<{ tasks: TaskItem[]; summary: { total: number; open: number; delayed: number; dueSoon: number } }>("/tasks"),
    refetchInterval: 90_000,
  });

  useEffect(() => {
    const onClick = (e: MouseEvent) => { if (ref.current && !ref.current.contains(e.target as Node)) setOpen(false); };
    document.addEventListener("mousedown", onClick);
    return () => document.removeEventListener("mousedown", onClick);
  }, []);

  const open_ = data?.summary.open ?? 0;
  const attention = (data?.summary.delayed ?? 0) + (data?.summary.dueSoon ?? 0);
  const preview = (data?.tasks ?? [])
    .filter((t) => t.scheduleHealth !== "done" && t.scheduleHealth !== "cancelled" && t.scheduleHealth !== "ahead")
    .slice(0, 8);

  return (
    <div className="relative" ref={ref}>
      <button className="btn-ghost relative !p-2" onClick={() => setOpen((o) => !o)} aria-label="My tasks">
        <CheckSquare size={18} />
        {open_ > 0 && (
          <span className={clsx(
            "absolute -right-0.5 -top-0.5 flex h-4 min-w-4 items-center justify-center rounded-full px-1 text-[10px] font-bold text-white",
            attention > 0 ? "bg-st-delayed" : "bg-brand-600",
          )}>
            {open_ > 9 ? "9+" : open_}
          </span>
        )}
      </button>
      {open && (
        <div className="absolute right-0 z-40 mt-2 w-[24rem] animate-fade-in rounded-xl border border-line bg-surface shadow-pop">
          <div className="flex items-center justify-between border-b border-line px-4 py-2.5">
            <span className="text-sm font-semibold">My tasks</span>
            <span className="text-xs text-ink-faint">
              {open_} open{attention > 0 ? ` · ${attention} need attention` : ""}
            </span>
          </div>
          <div className="max-h-[60vh] overflow-y-auto">
            {preview.length === 0 && <p className="px-4 py-8 text-center text-sm text-ink-faint">No open tasks assigned to you.</p>}
            {preview.map((t) => (
              <Link
                key={t.activityId}
                to={`/projects/${t.projectId}/schedule?activity=${t.activityId}`}
                onClick={() => setOpen(false)}
                className="flex items-start gap-3 border-b border-line px-4 py-3 last:border-0 hover:bg-surface-2"
              >
                <span className="min-w-0 flex-1">
                  <span className="block truncate text-sm font-medium">{t.title}</span>
                  <span className="mt-0.5 block truncate text-xs text-ink-faint">{t.projectName}</span>
                  <span className="mt-1 flex items-center gap-2">
                    <HealthBadge health={t.scheduleHealth} />
                    {t.endDate && <span className="text-[11px] text-ink-faint">Due {fmtDate(t.endDate)}</span>}
                  </span>
                </span>
              </Link>
            ))}
          </div>
          <Link to="/my-tasks" onClick={() => setOpen(false)} className="block border-t border-line px-4 py-2.5 text-center text-xs font-medium text-brand-600 hover:bg-surface-2">
            Open My Tasks
          </Link>
        </div>
      )}
    </div>
  );
}
